import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from 'components/Modal';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import { getApiErrorSnackbarDescription } from 'helpers/api-error-snackbar.helpers';
import Segment from 'helpers/segment';
import { selectActiveVenue } from 'selectors/general';

import { ADD_CATEGORY_MODAL_NAME } from './components/AddCategoryModal';
import { DELETE_CATEGORY_MODAL_NAME } from './components/DeleteCategoryModal';
import { DISABLE_CATEGORY_MODAL_NAME } from './components/DisableCategoryModal';
import { DUPLICATE_CATEGORY_MODAL_NAME } from './components/DuplicateCategoryModal';
import { EDIT_CATEGORY_MODAL_NAME } from './components/EditCategoryModal';
import { PARTNER_STATE } from './SkuItems.constants';
import { SKU_ANALYTICS_EVENTS } from './SkuAnalytics.constants';
import { getSkuVenueSource } from './SkuVenueSource.helpers';
import messages from './SkuMenuPage.messages';
import useSkuMutations from './useSkuMutations';

const strong = chunks => <strong>{chunks}</strong>;

const useSkuCategoryActions = ({ venueId, menus, selectedMenuId, refetch }) => {
  const dispatch = useDispatch();
  const venue = useSelector(selectActiveVenue);
  const source = getSkuVenueSource(venue);
  const { toggleCategory, deleteCategory } = useSkuMutations({ venueId });

  const handleAddCategory = useCallback(() => {
    dispatch(openModal(ADD_CATEGORY_MODAL_NAME, { menus, venueId, defaultMenuId: selectedMenuId, onSuccess: refetch }));
  }, [dispatch, menus, venueId, selectedMenuId, refetch]);

  const handleEditCategory = useCallback(
    category => {
      dispatch(
        openModal(EDIT_CATEGORY_MODAL_NAME, {
          category: category.raw,
          menus,
          venueId,
        })
      );
    },
    [dispatch, menus, venueId]
  );

  const handleDuplicateCategory = useCallback(
    category => {
      dispatch(
        openModal(DUPLICATE_CATEGORY_MODAL_NAME, {
          category,
          menus,
          venueId,
          onSuccess: refetch,
        })
      );
    },
    [dispatch, menus, venueId, refetch]
  );

  const handleToggleCategory = useCallback(
    async category => {
      const categoryName = category.name;
      const categoryId = category.raw?.id || category.id;
      const isEnabled = category.enabled !== false;

      if (isEnabled) {
        dispatch(
          openModal(DISABLE_CATEGORY_MODAL_NAME, {
            categoryName,
            onConfirmDisable: () =>
              toggleCategory.mutateAsync(
                {
                  categoryId,
                  optimistic: { enabled: false, partnerState: PARTNER_STATE.UNAVAILABLE },
                  payload: { enabled: false, partnerState: PARTNER_STATE.UNAVAILABLE },
                },
                {
                  onSuccess: () => {
                    Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_AVAILABILITY_CHANGED, {
                      venueId,
                      categoryId,
                      enabled: false,
                      source,
                    });
                    dispatch(
                      createSuccessSnackbar(
                        messages.categoryDisableSuccess,
                        { categoryName, strong },
                        { isLowStyle: true }
                      )
                    );
                  },
                  onError: error => {
                    dispatch(
                      createErrorSnackbar(
                        messages.categoryDisableError,
                        { strong },
                        { isLowStyle: true },
                        getApiErrorSnackbarDescription(error)
                      )
                    );
                  },
                }
              ),
          })
        );
      } else {
        toggleCategory.mutate(
          {
            categoryId,
            optimistic: { enabled: true, partnerState: null },
            payload: { enabled: true, partnerState: null },
          },
          {
            onSuccess: () => {
              Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_AVAILABILITY_CHANGED, {
                venueId,
                categoryId,
                enabled: true,
                source,
              });
              dispatch(
                createSuccessSnackbar(messages.categoryEnableSuccess, { categoryName, strong }, { isLowStyle: true })
              );
            },
            onError: error => {
              dispatch(
                createErrorSnackbar(
                  messages.categoryEnableError,
                  { strong },
                  { isLowStyle: true },
                  getApiErrorSnackbarDescription(error)
                )
              );
            },
          }
        );
      }
    },
    [dispatch, toggleCategory, venueId, source]
  );

  const handleDeleteCategory = useCallback(
    category => {
      const categoryName = category.name;
      const categoryId = category.raw?.id || category.id;
      const items = category.items || [];
      const itemIds = items.map(item => item.id);

      const belongsToAnotherCategory = item => (item.categoryIds || []).some(id => String(id) !== String(category.id));
      const movedItemNames = items.filter(item => !belongsToAnotherCategory(item)).map(item => item.name);
      const keptItemNames = items.filter(belongsToAnotherCategory).map(item => item.name);

      dispatch(
        openModal(DELETE_CATEGORY_MODAL_NAME, {
          categoryName,
          movedItemNames,
          keptItemNames,
          onConfirmDelete: () =>
            deleteCategory.mutateAsync(
              { categoryId, itemIds },
              {
                onSuccess: () => {
                  Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_DELETED, {
                    venueId,
                    categoryId,
                    itemCount: itemIds.length,
                    source,
                  });
                  dispatch(
                    createSuccessSnackbar(
                      messages.categoryDeleteSuccess,
                      { categoryName, strong },
                      { isLowStyle: true }
                    )
                  );
                },
                onError: error => {
                  dispatch(
                    createErrorSnackbar(
                      messages.categoryDeleteError,
                      { strong },
                      { isLowStyle: true },
                      getApiErrorSnackbarDescription(error)
                    )
                  );
                },
              }
            ),
        })
      );
    },
    [dispatch, deleteCategory, venueId, source]
  );

  const handleViewCategory = useCallback(
    category => {
      dispatch(
        openModal(EDIT_CATEGORY_MODAL_NAME, {
          category: category.raw,
          menus,
          venueId,
          isReadOnly: true,
        })
      );
    },
    [dispatch, menus, venueId]
  );

  return {
    handleAddCategory,
    handleEditCategory,
    handleDuplicateCategory,
    handleToggleCategory,
    handleDeleteCategory,
    handleViewCategory,
  };
};

export default useSkuCategoryActions;
